import { Plus, Paperclip, FlaskConical, Database } from "lucide-react";
import { type FC, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

const FileUpload: FC<FileUploadProps> = ({ onFilesSelected, disabled = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  // Position menu above the button and close on outside click
  useEffect(() => {
    if (!menuOpen) return;

    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.top, left: rect.left });
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setMenuOpen((v) => !v)}
        className={`w-6 h-6 rounded-full bg-white flex items-center justify-center shadow ${
          disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : 'hover:bg-gray-50 cursor-pointer'
        }`}
      >
        <Plus size={16} />
      </button>

      {menuOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
          style={{ top: menuPos.top, left: menuPos.left, transform: 'translateY(-100%) translateY(-8px)', zIndex: 9999 }}
        >
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => {
              setMenuOpen(false);
              fileInputRef.current?.click();
            }}
          >
            <Paperclip size={15} />
            Attach a file
          </button>
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-400 hover:bg-gray-100 cursor-not-allowed"
            onClick={() => setMenuOpen(false)}
          >
            <img src="/icons/aveva-pi.png" alt="AVEVA PI" className="w-4 h-4 object-contain shrink-0" />
            AVEVA PI
            <span className="ml-auto text-[10px] text-gray-300">soon</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-400 hover:bg-gray-100 cursor-not-allowed"
            onClick={() => setMenuOpen(false)}
          >
            <FlaskConical size={15} />
            LIMS
            <span className="ml-auto text-[10px] text-gray-300">soon</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-400 hover:bg-gray-100 cursor-not-allowed"
            onClick={() => setMenuOpen(false)}
          >
            <img src="/icons/onedrive.png" alt="OneDrive" className="w-4 h-4 object-contain shrink-0" />
            OneDrive
            <span className="ml-auto text-[10px] text-gray-300">soon</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-400 hover:bg-gray-100 cursor-not-allowed"
            onClick={() => setMenuOpen(false)}
          >
            <Database size={15} />
            SQL Database
            <span className="ml-auto text-[10px] text-gray-300">soon</span>
          </button>
        </div>,
        document.body
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        disabled={disabled}
        onChange={(e) => {
          if (disabled) return;
          const files = e.target.files;
          if (!files) return;
          onFilesSelected(Array.from(files));
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default FileUpload;

export function getFileIcon(file: File) {
  const name = file.name.toLowerCase();
  if (name.match(/\.(png|jpe?g|gif|bmp|webp|svg)$/)) return <img src="/icons/image.png" alt="image" className="w-4 h-4 inline-block mr-1" />;
  if (name.endsWith('.pdf')) return <img src="/icons/pdf.png" alt="pdf" className="w-4 h-4 inline-block mr-1" />;
  if (name.match(/\.(csv|xls|xlsx)$/)) return <img src="/icons/table.png" alt="table" className="w-4 h-4 inline-block mr-1" />;
  return <img src="/icons/paperclip.png" alt="attachment" className="w-4 h-4 inline-block mr-1" />;
}


// Helper component for attached files display
export function AttachedFilesList({ 
  attachedFiles, 
  onRemoveFile 
}: { 
  attachedFiles: File[], 
  onRemoveFile: (name: string) => void 
}) {
  if (attachedFiles.length === 0) return null;

  return (
    <div className="flex gap-1 flex-wrap mb-1">
      {attachedFiles.map((file, idx) => (
        <span key={idx} className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs mr-1">
          {getFileIcon(file)} {file.name}
          <button
            type="button"
            className="ml-1 text-blue-500 hover:text-red-500 focus:outline-none"
            onClick={() => onRemoveFile(file.name)}
            tabIndex={-1}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

// Helper component for upload status
export function UploadStatus({ isUploading }: { isUploading: boolean }) {
  if (!isUploading) return null;

  return (
    <div className="flex items-center gap-2 text-xs text-blue-700 mt-2">
      <svg className="animate-spin h-4 w-4 mr-1 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
      Uploading files...
    </div>
  );
}